import { AFFILIATE_ERROR_CODES } from "../../affiliate/contract";
import { apiErrorCode, extractApiError } from "../../lib/apiErrors";

/**
 * An affiliate refusal, said in the reader's language.
 *
 * The wire carries `detail = {code, message}` and the contract is explicit that the SPA
 * maps the CODE and **never renders `message`** — that string is English and unlocalisable,
 * so printing it hands a French practitioner a developer diagnostic. So:
 *
 *   · a code in the closed set   -> `<prefix>.error.<CODE>`, a sentence we have written;
 *   · a code outside it          -> `<prefix>.error.UNKNOWN`. A backend that grows a refusal
 *     this build predates must degrade to "we cannot say what went wrong", not to its own
 *     prose;
 *   · NO code at all (a dropped connection, a 500 with an HTML body, pydantic's 422 array)
 *     -> `extractApiError`, the one reader that flattens those shapes safely and translates
 *     pydantic's English away.
 *
 * `prefix` is the key family of the reader's namespace — `aff` in the practitioner's
 * dictionary, `saff` in the operator's — because the two planes say the same refusal to
 * different people. `tv` is the `common`-bound translator `extractApiError` needs for the
 * keys IT owns; `fallback` is already-resolved text, so the caller picks between
 * `errors.loadFailed` and `errors.saveFailed` rather than this file guessing which.
 *
 * @param {any} e the axios error
 * @param {{t: function, tv: function, prefix?: string, fallback?: string}} opts
 * @returns {string}
 */
export function affiliateErrorText(e, { t, tv, prefix = "aff", fallback = "" }) {
  const code = apiErrorCode(e);
  if (code) {
    return t(AFFILIATE_ERROR_CODES.includes(code) ? `${prefix}.error.${code}` : `${prefix}.error.UNKNOWN`);
  }
  return extractApiError(e, fallback, tv);
}

export default affiliateErrorText;
